import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { translations, TranslationKey } from '@/translations/translations';

type Language = 'en' | 'de';

export interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Initialize language from URL param, localStorage or browser preference
    // Initialize language with a default that matches server-rendered HTML (SSR/Prerendering)
    // We check the URL immediately during initialization so the first client render matches the server output.
    const [language, setLanguageState] = useState<Language>(() => {
        if (typeof window === 'undefined') return 'en';
        const params = new URLSearchParams(window.location.search);
        const langParam = params.get('lang');
        if (langParam === 'en' || langParam === 'de') return langParam as Language;
        return 'en';
    });

    // Handle initial client-side language detection after hydration
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const langParam = params.get('lang');

        if (langParam === 'en' || langParam === 'de') {
            setLanguageState(langParam as Language);
            return;
        }

        const stored = localStorage.getItem('language');
        if (stored === 'en' || stored === 'de') {
            setLanguageState(stored as Language);
        }
    }, []);

    // Persist language preference
    useEffect(() => {
        localStorage.setItem('language', language);
    }, [language]);

    const setLanguage = useCallback((lang: Language) => {
        setLanguageState(lang);
    }, []);

    const t = useCallback((key: TranslationKey): string => {
        return translations[language][key] || key;
    }, [language]);

    const value = useMemo(() => ({
        language,
        setLanguage,
        t
    }), [language, setLanguage, t]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
